import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Plus, Trash2, Check, Calendar as CalIcon, X, Crown, Brain, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STORAGE_KEY = 'teko-mental-clarity-v2';

// The three deck zones, in left → right visual order.
const ZONES = [
  { id: 'postergable', label: 'Postergable', shortLabel: 'Postergable' },
  { id: 'prioridad',   label: 'Prioridad',   shortLabel: 'Prioridad' },
  { id: 'ruido',       label: 'Ruido',       shortLabel: 'Ruido' },
];

const ZONE_IDS = ZONES.map((z) => z.id);
const uid = () => `mc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// =============================================================================
// Idea inside a zone
// =============================================================================
const IdeaRow = ({ b, theme, tone, onToggle, onDelete, onSendToCalendar }) => (
  <div
    className="flex items-center gap-3 rounded-2xl px-3.5 py-3 mb-2.5 group"
    style={{
      background: theme.elevated,
      border: `1px solid ${theme.border}`,
    }}
    data-testid={`clarity-idea-${b.id}`}
  >
    <button
      onClick={() => onToggle(b.id)}
      className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
      style={{
        border: `2px solid ${b.done ? tone : theme.textMuted}`,
        background: b.done ? tone : 'transparent',
        color: theme.bg,
      }}
      aria-label="Marcar"
    >
      {b.done && <Check size={13} strokeWidth={3} />}
    </button>
    <span
      className="flex-1 text-sm leading-snug break-words"
      style={{
        color: b.done ? theme.textMuted : theme.textPrimary,
        textDecoration: b.done ? 'line-through' : 'none',
      }}
    >
      {b.text}
    </span>
    <div className="flex gap-0.5 shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
      <button
        onClick={() => onSendToCalendar(b)}
        className="p-1.5"
        style={{ color: theme.textMuted }}
        title="Mandar al calendario"
        aria-label="Mandar al calendario"
      >
        <CalIcon size={14} />
      </button>
      <button
        onClick={() => onDelete(b.id)}
        className="p-1.5"
        style={{ color: theme.textMuted }}
        title="Eliminar"
        aria-label="Eliminar"
      >
        <Trash2 size={14} />
      </button>
    </div>
  </div>
);

// =============================================================================
// Zone card. depth=0 = front (large, opaque). depth>0 = side (small, translucent).
// Memoized to keep input focus stable across re-renders.
// =============================================================================
const ZoneCard = React.memo(({
  zone, items, isFront, side, theme, tone, onSelect, onAddHere,
  onToggle, onDelete, onSendToCalendar, draftValue, onDraftChange, onDraftSubmit,
}) => {
  // Side cards hide on mobile (single-zone view), translucent + smaller on tablet+.
  const opacity = isFront ? 1 : 0.55;
  const scale = isFront ? 1 : 0.88;
  const xOffset = isFront ? 0 : side === 'left' ? 30 : -30;
  const zIndex = isFront ? 30 : 10;
  // Strong text shadow keeps pill legible on light theme tones.
  const pillTextShadow = '0 1px 2px rgba(0,0,0,0.45)';

  return (
    <div
      onClick={() => !isFront && onSelect(zone.id)}
      role={isFront ? undefined : 'button'}
      tabIndex={isFront ? undefined : 0}
      onKeyDown={(e) => { if (!isFront && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelect(zone.id); } }}
      className={`${isFront ? 'flex' : 'hidden sm:flex'} flex-col rounded-3xl overflow-hidden transition-all duration-500 ${isFront ? '' : 'cursor-pointer hover:opacity-75'}`}
      style={{
        background: theme.card,
        border: `1px solid ${theme.border}`,
        opacity,
        transform: `scale(${scale}) translateX(${xOffset}px)`,
        zIndex,
        flex: isFront ? '2 1 0' : '1 1 0',
        minWidth: 0,
        boxShadow: isFront
          ? `0 24px 60px ${tone}30, 0 0 0 1px ${tone}30`
          : `0 8px 20px rgba(0,0,0,0.35)`,
      }}
      data-testid={`clarity-zone-${zone.id}`}
      data-front={isFront ? '1' : '0'}
    >
      {/* Header pill */}
      <div className="px-4 sm:px-5 pt-5 pb-3" style={{ pointerEvents: isFront ? 'auto' : 'none' }}>
        <div
          className="inline-block px-5 py-2 rounded-2xl"
          style={{
            background: tone,
            boxShadow: `0 4px 16px ${tone}50`,
          }}
        >
          <h3
            className="font-semibold tracking-tight text-white text-base sm:text-lg"
            style={{ textShadow: pillTextShadow }}
          >
            {isFront ? zone.label : zone.shortLabel}
          </h3>
        </div>
      </div>

      {/* Body — more breathing room on mobile (was min-h 280, now 360) */}
      <div
        className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4 min-h-[360px] sm:min-h-[320px]"
        style={{ pointerEvents: isFront ? 'auto' : 'none' }}
      >
        {items.length === 0 ? (
          <p className="text-[11px] text-center mt-6 leading-relaxed" style={{ color: theme.textMuted }}>
            {isFront ? 'Texto ej:' : 'Texto ej:'}
          </p>
        ) : (
          items.map((b) => (
            <IdeaRow
              key={b.id}
              b={b}
              theme={theme}
              tone={tone}
              onToggle={onToggle}
              onDelete={onDelete}
              onSendToCalendar={onSendToCalendar}
            />
          ))
        )}
      </div>

      {/* Inline add — only shown on the front zone */}
      {isFront && (
        <div className="px-4 sm:px-5 pb-5 pt-3" style={{ borderTop: `1px solid ${theme.border}` }}>
          <form
            onSubmit={(e) => { e.preventDefault(); onDraftSubmit(); }}
            className="flex items-center gap-2"
          >
            <input
              value={draftValue}
              onChange={(e) => onDraftChange(e.target.value)}
              placeholder={`Agregá a "${zone.label}"...`}
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              className="flex-1 bg-transparent border-0 outline-none text-sm px-4 py-2.5 rounded-full"
              style={{
                background: theme.elevated,
                border: `1px solid ${theme.border}`,
                color: theme.textPrimary,
              }}
              data-testid={`clarity-input-${zone.id}`}
            />
            <button
              type="submit"
              className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-transform hover:scale-105"
              style={{
                background: tone,
                color: theme.bg,
                boxShadow: `0 4px 16px ${tone}50`,
                textShadow: '0 1px 2px rgba(0,0,0,0.45)',
              }}
              data-testid={`clarity-submit-${zone.id}`}
              aria-label="Agregar"
            >
              <Plus size={18} strokeWidth={2.5} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
});
ZoneCard.displayName = 'ZoneCard';

// =============================================================================
// Page
// =============================================================================
const MentalClarity = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const ideasLimit = user?.plan_limits?.clarity_ideas;
  const isUnlimited = ideasLimit === null || ideasLimit === undefined;

  // Migrate legacy v1 storage on first load.
  const [items, setItems] = useState(() => {
    try {
      const fresh = localStorage.getItem(STORAGE_KEY);
      if (fresh) return JSON.parse(fresh);
      const legacy = localStorage.getItem('teko-mental-clarity');
      if (legacy) {
        const arr = JSON.parse(legacy);
        // Map old zones to new (inbox → prioridad).
        return arr.map((b) => ({ ...b, zone: b.zone === 'inbox' ? 'prioridad' : b.zone }));
      }
    } catch (err) { console.debug(err); }
    return [];
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }
    catch (err) { console.debug(err); }
  }, [items]);

  // Which zone is in the front of the deck (default = Prioridad).
  const [frontIdx, setFrontIdx] = useState(1);
  const frontZone = ZONES[frontIdx];

  // Tones derived from the active theme.
  const zoneTone = useCallback((zoneId) => {
    if (zoneId === 'prioridad') return theme.accent;
    if (zoneId === 'postergable') return theme.primary;
    if (zoneId === 'ruido') return theme.warning || theme.accent;
    return theme.accent;
  }, [theme]);

  // Drafts per zone (only the front one is editable, but each remembers its own draft).
  const [drafts, setDrafts] = useState({ postergable: '', prioridad: '', ruido: '' });
  const setDraft = (zoneId, v) => setDrafts((d) => ({ ...d, [zoneId]: v }));

  const addIdea = (zoneId) => {
    const txt = (drafts[zoneId] || '').trim();
    if (!txt) return;
    if (!isUnlimited && items.length >= ideasLimit) {
      toast.error(`Tu plan permite máximo ${ideasLimit} ideas en Claridad Mental.`);
      return;
    }
    setItems((prev) => [...prev, { id: uid(), text: txt, zone: zoneId, done: false }]);
    setDrafts((d) => ({ ...d, [zoneId]: '' }));
  };

  const deleteIdea = (id) => setItems((prev) => prev.filter((b) => b.id !== id));
  const toggleIdea = (id) => setItems((prev) => prev.map((b) => (b.id === id ? { ...b, done: !b.done } : b)));
  const clearAll = () => {
    if (window.confirm('¿Vaciar TODAS las ideas?')) setItems([]);
  };

  const sendToCalendar = async (b) => {
    const dt = new Date();
    dt.setHours(dt.getHours() + 1, 0, 0, 0);
    const end = new Date(dt.getTime() + 30 * 60 * 1000);
    try {
      await api.post('/calendar', {
        title: b.text,
        category: 'personal',
        start_time: dt.toISOString(),
        end_time: end.toISOString(),
        description: 'Desde Claridad Mental',
      });
      toast.success('Mandado al calendario · ' + dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      deleteIdea(b.id);
    } catch (err) {
      toast.error('No pude mandarlo al calendario');
      console.debug(err);
    }
  };

  // Items grouped by zone — memoized for stable references.
  const byZone = useMemo(() => {
    const m = { postergable: [], prioridad: [], ruido: [] };
    items.forEach((b) => { if (m[b.zone]) m[b.zone].push(b); });
    return m;
  }, [items]);

  // Swipe / drag controller — touch on mobile, mouse on desktop.
  const dragState = useRef(null);
  const containerRef = useRef(null);
  const onPointerDown = (e) => {
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    dragState.current = { startX: x, hasMoved: false };
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    if (Math.abs(x - dragState.current.startX) > 5) dragState.current.hasMoved = true;
  };
  const onPointerUp = (e) => {
    if (!dragState.current) return;
    const x = (e.changedTouches ? e.changedTouches[0].clientX : e.clientX) || dragState.current.startX;
    const dx = x - dragState.current.startX;
    if (Math.abs(dx) > 60) {
      // Swipe left → next zone; swipe right → previous zone.
      setFrontIdx((prev) => {
        const next = dx < 0 ? prev + 1 : prev - 1;
        return Math.max(0, Math.min(ZONES.length - 1, next));
      });
    }
    dragState.current = null;
  };

  // Keyboard arrow shortcuts.
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.target.tagName === 'INPUT') return;
      if (ev.key === 'ArrowLeft') setFrontIdx((p) => Math.max(0, p - 1));
      if (ev.key === 'ArrowRight') setFrontIdx((p) => Math.min(ZONES.length - 1, p + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="space-y-5" data-testid="mental-clarity-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Brain size={18} style={{ color: theme.accent }} />
            <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: theme.textMuted }}>Claridad Mental</p>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight" style={{ color: theme.accent }}>
            Vaciá tu mente
          </h1>
          <p className="text-xs sm:text-sm mt-1" style={{ color: theme.textSecondary }}>
            Deslizá entre las zonas. La <span style={{ color: theme.accent, fontWeight: 600 }}>Prioridad</span> manda — todo lo demás es ruido o postergable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] tabular-nums" style={{ color: !isUnlimited && items.length >= ideasLimit ? theme.error : theme.textMuted }}>
            <span style={{ color: theme.textPrimary, fontWeight: 600 }}>{items.length}</span> / {isUnlimited ? '∞' : ideasLimit} ideas
          </span>
          {!isUnlimited && items.length >= ideasLimit && (
            <Link to="/membership" className="inline-flex items-center gap-1 underline text-[11px]" style={{ color: theme.accent }}>
              <Crown size={11} /> Subir plan
            </Link>
          )}
          {items.length > 0 && (
            <Button onClick={clearAll} size="sm" variant="outline"
              style={{ borderColor: theme.border, color: theme.textMuted }} data-testid="clarity-clear-all">
              <X size={12} className="mr-1" /> Vaciar
            </Button>
          )}
        </div>
      </div>

      {/* Zone indicator (mobile) */}
      <div className="flex sm:hidden items-center justify-center gap-2">
        <button onClick={() => setFrontIdx((p) => Math.max(0, p - 1))}
          disabled={frontIdx === 0}
          className="p-1.5 rounded-full disabled:opacity-30"
          style={{ background: theme.elevated, color: theme.textPrimary, border: `1px solid ${theme.border}` }}
          data-testid="clarity-prev-zone">
          <ChevronLeft size={16} />
        </button>
        <div className="flex gap-1">
          {ZONES.map((z, i) => (
            <button key={z.id} onClick={() => setFrontIdx(i)}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === frontIdx ? 24 : 8,
                background: i === frontIdx ? zoneTone(z.id) : theme.border,
              }}
              aria-label={z.label} />
          ))}
        </div>
        <button onClick={() => setFrontIdx((p) => Math.min(ZONES.length - 1, p + 1))}
          disabled={frontIdx === ZONES.length - 1}
          className="p-1.5 rounded-full disabled:opacity-30"
          style={{ background: theme.elevated, color: theme.textPrimary, border: `1px solid ${theme.border}` }}
          data-testid="clarity-next-zone">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Deck — front zone is always visually centered, the other 2 sit on left/right */}
      <div
        ref={containerRef}
        className="flex items-stretch justify-center gap-2 sm:gap-4 px-0 sm:px-6 py-2 select-none touch-pan-y"
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={() => { dragState.current = null; }}
        onTouchStart={onPointerDown}
        onTouchMove={onPointerMove}
        onTouchEnd={onPointerUp}
        style={{ perspective: '1200px' }}
        data-testid="clarity-deck"
      >
        {(() => {
          // Build the visual order so the front zone is in the middle column.
          const others = ZONES.filter((_, i) => i !== frontIdx);
          const visualOrder = [others[0], frontZone, others[1]];
          return visualOrder.map((zone, visualIdx) => {
            const isFront = visualIdx === 1; // middle is always the front
            const side = visualIdx === 0 ? 'left' : 'right';
            return (
              <ZoneCard
                key={zone.id}
                zone={zone}
                items={byZone[zone.id]}
                isFront={isFront}
                side={side}
                theme={theme}
                tone={zoneTone(zone.id)}
                onSelect={(zid) => setFrontIdx(ZONE_IDS.indexOf(zid))}
                onAddHere={() => addIdea(zone.id)}
                onToggle={toggleIdea}
                onDelete={deleteIdea}
                onSendToCalendar={sendToCalendar}
                draftValue={drafts[zone.id] || ''}
                onDraftChange={(v) => setDraft(zone.id, v)}
                onDraftSubmit={() => addIdea(zone.id)}
              />
            );
          });
        })()}
      </div>

      <p className="text-center text-[11px]" style={{ color: theme.textMuted }}>
        En compu deslizá con el mouse · En celular con el dedo · Tap en una zona del costado para traerla al frente
      </p>
    </div>
  );
};

export default MentalClarity;
